import {
  Alert,
  Button,
  message,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import backfillMarketValueApiCall from 'apps/wealth/apiCalls/programs/backfillMarketValue';
import syncCashBalanceApiCall from 'apps/wealth/apiCalls/programs/syncCashBalance';
import calcFeesApiCall from 'apps/wealth/apiCalls/fees/calc';
import listFeesApiCall from 'apps/wealth/apiCalls/fees/list';
import { useFetchApiList, usePostApi } from 'hooks/api';
import { DatabaseIcon, DownloadCloudIcon, ArrowDownCircleIcon, ReceiptIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link } from 'react-router-dom';
import { formatDateTime } from 'utils/formatDate';

const fmtNum = (v: number | null | undefined, decimals = 2) => {
  if (v == null || isNaN(v)) return '—';
  return v.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const pct = (v: number | null | undefined, decimals = 2) =>
  v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(decimals)}%` : '—';

const pctColor = (v: number | null | undefined) =>
  v == null ? '' : v >= 0 ? 'text-green-600' : 'text-red-500';

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">{label}</div>
      <div className="text-sm font-semibold text-gray-800">{value}</div>
    </div>
  );
}

// ─── Market Value History Chart ───────────────────────────────────────────────

interface MarketValueChartProps {
  data: any[];
}

const MarketValueChart = ({ data }: MarketValueChartProps) => {
  const chartData = data.map((d: any) => ({
    date: d.date,
    value: d.market_value ?? 0,
    portfolioValue: d.portfolio_value ?? 0,
    cashBalance: d.cash_balance ?? 0,
    navPerUnit: d.nav_per_unit ?? 0,
    unitsOutstanding: d.units_outstanding ?? 0,
    buyAmount: d.buy_amount ?? 0,
    sellAmount: d.sell_amount ?? 0,
    cashInflow: d.cash_inflow ?? 0,
    cashOutflow: d.cash_outflow ?? 0,
    actual: d.is_actual,
  }));

  const nameMap: Record<string, string> = {
    value: 'Market Value',
    portfolioValue: 'Portfolio Value',
    cashBalance: 'Cash Balance',
    navPerUnit: 'NAV / Unit',
    unitsOutstanding: 'Units',
    buyAmount: 'Buy',
    sellAmount: 'Sell',
    cashInflow: 'Cash In',
    cashOutflow: 'Cash Out',
  };

  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={chartData} margin={{ top: 8, right: 112, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11, fill: '#888' }}
          tickLine={false}
          axisLine={false}
          interval="preserveStartEnd"
        />
        {/* Left axis — market value */}
        <YAxis
          yAxisId="mv"
          tick={{ fontSize: 11, fill: '#888' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => `${(v / 1_000_000).toFixed(0)}M`}
          width={56}
        />
        {/* Right inner axis — daily buy / sell / cash flow amounts */}
        <YAxis
          yAxisId="trade"
          orientation="right"
          tick={{ fontSize: 11, fill: '#888' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => `${(v / 1_000_000).toFixed(0)}M`}
          width={56}
        />
        {/* Right outer axis — NAV per unit (different scale from M values) */}
        <YAxis
          yAxisId="nav"
          orientation="right"
          tick={{ fontSize: 11, fill: '#f97316' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          width={56}
        />
        {/* Hidden axis for units outstanding — own scale, no labels */}
        <YAxis yAxisId="units" orientation="right" hide />
        <RechartsTooltip
          formatter={(v: any, name: string) => [fmtNum(v as number, 0), nameMap[name] ?? name]}
          labelFormatter={(label: string) => label}
        />
        <Legend iconType="plainline" iconSize={16} wrapperStyle={{ fontSize: 11 }} />
        {/* Buy bars */}
        <Bar
          yAxisId="trade"
          dataKey="buyAmount"
          name="Buy"
          fill="#10b981"
          opacity={0.5}
          maxBarSize={14}
        />
        {/* Sell bars */}
        <Bar
          yAxisId="trade"
          dataKey="sellAmount"
          name="Sell"
          fill="#ef4444"
          opacity={0.5}
          maxBarSize={14}
        />
        {/* Cash inflow bars */}
        <Bar
          yAxisId="trade"
          dataKey="cashInflow"
          name="Cash In"
          fill="#6366f1"
          opacity={0.6}
          maxBarSize={14}
        />
        {/* Cash outflow bars */}
        <Bar
          yAxisId="trade"
          dataKey="cashOutflow"
          name="Cash Out"
          fill="#f59e0b"
          opacity={0.6}
          maxBarSize={14}
        />
        {/* Cash balance line */}
        <Line
          yAxisId="mv"
          type="monotone"
          dataKey="cashBalance"
          name="Cash Balance"
          stroke="#f59e0b"
          strokeWidth={1.5}
          strokeDasharray="4 2"
          dot={false}
          activeDot={{ r: 3, fill: '#f59e0b' }}
        />
        {/* Portfolio value line (stock/ETF holdings only) */}
        <Line
          yAxisId="mv"
          type="monotone"
          dataKey="portfolioValue"
          name="Portfolio Value"
          stroke="#6366f1"
          strokeWidth={1.5}
          strokeDasharray="4 2"
          dot={false}
          activeDot={{ r: 3, fill: '#6366f1' }}
        />
        {/* NAV per unit line — right outer axis, orange */}
        <Line
          yAxisId="nav"
          type="monotone"
          dataKey="navPerUnit"
          name="NAV / Unit"
          stroke="#f97316"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 3, fill: '#f97316' }}
        />
        {/* Units outstanding — hidden axis, scaled to its own range */}
        <Line
          yAxisId="units"
          type="monotone"
          dataKey="unitsOutstanding"
          name="Units"
          stroke="#64748b"
          strokeWidth={1.5}
          strokeDasharray="3 3"
          dot={false}
          activeDot={{ r: 3, fill: '#64748b' }}
        />
        {/* Market value line (portfolio + cash) — on top */}
        <Line
          yAxisId="mv"
          type="monotone"
          dataKey="value"
          name="Market Value"
          stroke="#10b981"
          strokeWidth={2}
          dot={(props: any) => {
            const { cx, cy, payload } = props;
            if (payload.actual) return <g key={`dot-mv-${cx}-${cy}`} />;
            return (
              <circle
                key={`dot-mv-${cx}-${cy}`}
                cx={cx}
                cy={cy}
                r={2}
                fill="#a7f3d0"
                stroke="white"
                strokeWidth={1}
              />
            );
          }}
          activeDot={{ r: 4, fill: '#10b981' }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const portfolioDailyColumns = [
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    sorter: (a: any, b: any) => a.date.localeCompare(b.date),
    defaultSortOrder: 'ascend' as const,
    render: (v: string, record: any) => (
      <span className="font-mono">
        {v}
        {!record.is_actual && (
          <Tag color="default" className="ml-1 text-[10px]">
            carry-fwd
          </Tag>
        )}
      </span>
    ),
  },
  {
    title: 'Buy',
    dataIndex: 'buy_amount',
    key: 'buy_amount',
    align: 'right' as const,
    render: (v: number) => (
      <span className={`font-mono ${v > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>
        {fmtNum(v ?? 0, 0)}
      </span>
    ),
  },
  {
    title: 'Sell',
    dataIndex: 'sell_amount',
    key: 'sell_amount',
    align: 'right' as const,
    render: (v: number) => (
      <span className={`font-mono ${v > 0 ? 'text-red-500' : 'text-gray-300'}`}>
        {fmtNum(v ?? 0, 0)}
      </span>
    ),
  },
  {
    title: 'Cash In',
    dataIndex: 'cash_inflow',
    key: 'cash_inflow',
    align: 'right' as const,
    render: (v: number) => (
      <span className={`font-mono ${v > 0 ? 'text-indigo-600' : 'text-gray-300'}`}>
        {fmtNum(v ?? 0, 0)}
      </span>
    ),
  },
  {
    title: 'Cash Out',
    dataIndex: 'cash_outflow',
    key: 'cash_outflow',
    align: 'right' as const,
    render: (v: number) => (
      <span className={`font-mono ${v > 0 ? 'text-amber-600' : 'text-gray-300'}`}>
        {fmtNum(v ?? 0, 0)}
      </span>
    ),
  },
  {
    title: 'Cash Balance',
    dataIndex: 'cash_balance',
    key: 'cash_balance',
    align: 'right' as const,
    render: (v: number) => <span className="font-mono">{fmtNum(v ?? 0, 0)}</span>,
  },
  {
    title: 'Portfolio Value',
    dataIndex: 'portfolio_value',
    key: 'portfolio_value',
    align: 'right' as const,
    render: (v: number) => <span className="font-mono">{fmtNum(v ?? 0, 0)}</span>,
  },
  {
    title: 'Market Value',
    dataIndex: 'market_value',
    key: 'market_value',
    align: 'right' as const,
    render: (v: number) => <span className="font-mono font-medium">{fmtNum(v ?? 0, 0)}</span>,
  },
  {
    title: 'Units',
    dataIndex: 'units_outstanding',
    key: 'units_outstanding',
    align: 'right' as const,
    render: (v: number) => <span className="font-mono">{fmtNum(v ?? 0, 2)}</span>,
  },
  {
    title: 'NAV / Unit',
    dataIndex: 'nav_per_unit',
    key: 'nav_per_unit',
    align: 'right' as const,
    render: (v: number) => <span className="font-mono text-orange-600">{fmtNum(v ?? 0, 2)}</span>,
  },
];

// ─── Balance history (raw deposit/withdrawal ledger from NHSV) ────────────────

const balanceHistoryColumns = [
  {
    title: 'Date',
    dataIndex: 'trade_date',
    key: 'trade_date',
    sorter: (a: any, b: any) => a.trade_date.localeCompare(b.trade_date),
    defaultSortOrder: 'ascend' as const,
    render: (v: string) => <span className="font-mono">{v}</span>,
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    render: (v: number) =>
      v === 1001
        ? <Tag color="blue">Deposit</Tag>
        : <Tag color="volcano">Withdrawal</Tag>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (v: number) =>
      v === 1002
        ? <Tag color="green">Success</Tag>
        : <Tag color="orange">Pending</Tag>,
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    align: 'right' as const,
    render: (v: number, record: any) => (
      <span className={`font-mono ${record.type === 1002 ? 'text-red-500' : 'text-emerald-600'}`}>
        {record.type === 1002 ? '−' : '+'}{fmtNum(v ?? 0, 0)}
      </span>
    ),
  },
  {
    title: 'Balance After',
    dataIndex: 'balance',
    key: 'balance',
    align: 'right' as const,
    render: (v: number) => <span className="font-mono">{fmtNum(v ?? 0, 0)}</span>,
  },
  {
    title: 'Note',
    dataIndex: 'note',
    key: 'note',
    render: (v: string) => (
      <span className="text-xs text-gray-500">{v || '—'}</span>
    ),
  },
];


// ─── Advisor Fee Section ──────────────────────────────────────────────────────

const FEE_STATUS: Record<number, { label: string; color: string }> = {
  0: { label: '—',        color: 'default' },
  1: { label: 'Pending',  color: 'orange'  },
  2: { label: 'Invoiced', color: 'blue'    },
  3: { label: 'Paid',     color: 'green'   },
  4: { label: 'Refunded', color: 'cyan'    },
};

const feeColumns = [
  {
    title: 'Year',
    dataIndex: 'year',
    key: 'year',
    render: (v: number) => <span className="font-semibold">{v}</span>,
  },
  {
    title: 'NAV Base',
    dataIndex: 'nav_base',
    key: 'nav_base',
    align: 'right' as const,
    render: (v: number) => <span className="font-mono text-gray-500">{fmtNum(v, 2)}</span>,
  },
  {
    title: 'H1 Fee',
    dataIndex: 'h1_gross_fee',
    key: 'h1_gross_fee',
    align: 'right' as const,
    render: (v: number, row: any) =>
      row.h1_calculated_at ? (
        <span className={`font-mono font-semibold ${v > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
          {fmtNum(v, 0)}
        </span>
      ) : <span className="text-gray-300">—</span>,
  },
  {
    title: 'H1 Status',
    dataIndex: 'h1_status',
    key: 'h1_status',
    render: (v: number, row: any) => {
      if (!row.h1_calculated_at) return <span className="text-gray-300">—</span>;
      const s = FEE_STATUS[v] ?? FEE_STATUS[0];
      return <Tag color={s.color}>{s.label}</Tag>;
    },
  },
  {
    title: 'Year-End Fee',
    dataIndex: 'ye_gross_fee',
    key: 'ye_gross_fee',
    align: 'right' as const,
    render: (v: number, row: any) =>
      row.ye_calculated_at ? (
        <span className={`font-mono font-semibold ${v > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
          {fmtNum(v, 0)}
        </span>
      ) : <span className="text-gray-300">—</span>,
  },
  {
    title: 'Settlement',
    dataIndex: 'settlement_amount',
    key: 'settlement_amount',
    align: 'right' as const,
    render: (v: number, row: any) => {
      if (!row.ye_calculated_at) return <span className="text-gray-300">—</span>;
      return (
        <span className={`font-mono font-semibold ${v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-500' : 'text-gray-400'}`}>
          {v !== 0 ? `${v > 0 ? '+' : ''}${fmtNum(v, 0)}` : '—'}
        </span>
      );
    },
  },
  {
    title: 'YE Status',
    dataIndex: 'ye_status',
    key: 'ye_status',
    render: (v: number, row: any) => {
      if (!row.ye_calculated_at) return <span className="text-gray-300">—</span>;
      const s = FEE_STATUS[v] ?? FEE_STATUS[0];
      return <Tag color={s.color}>{s.label}</Tag>;
    },
  },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

const AdvisorFeeSection = ({ programId }: { programId: string }) => {
  const [date, setDate] = useState<string>(todayStr());

  const { data: fees, load, isLoading: isFetching } = useFetchApiList(listFeesApiCall, {
    resourceName: 'data',
  });

  useEffect(() => {
    load({ id: programId });
  }, [programId]);

  const { post: calcFees, isLoading: isCalcing } = usePostApi(calcFeesApiCall, {
    onSuccess: () => {
      load({ id: programId });
    },
    onError: () => {
      message.error('Fee calculation failed. Check server logs.');
    },
  });

  return (
    <>
      <h3 className="text-base font-medium text-gray-800 border-b pb-1 flex items-center gap-1 mt-2">
        <ReceiptIcon size={16} />
        Advisor Fees
      </h3>

      <div className="flex items-center gap-3">
        <div>
          <div className="mb-1 text-xs text-gray-500">As of date</div>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-8 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="pt-5">
          <Button
            type="primary"
            loading={isCalcing}
            onClick={() => calcFees({ id: programId, date })}
          >
            Calculate Fees
          </Button>
        </div>
        <div className="pt-5 text-xs text-gray-400">
          Calculates H1 (Jun 30) and year-end (Dec 31) checkpoints up to the selected date.
          Already-calculated records are skipped.
        </div>
      </div>

      <Table
        columns={feeColumns}
        dataSource={fees}
        rowKey="id"
        size="small"
        loading={isFetching || isCalcing}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No fee records yet — click Calculate Fees to generate.' }}
      />
    </>
  );
};

// ─── Main detail component ────────────────────────────────────────────────────

const ItemDetail = ({ item, onRefresh }: { item: any; onRefresh?: () => void }) => {
  const [mvBackfillResult, setMvBackfillResult] = useState<any>(null);

  const { post: triggerMvBackfill, isLoading: isMvBackfilling } = usePostApi(
    backfillMarketValueApiCall,
    {
      onSuccess: (data: any) => {
        const result = data?.data;
        setMvBackfillResult(result);
        message.success(
          `Market value backfill complete — ${result?.daily_rows ?? 0} daily rows`
        );
        if (onRefresh) onRefresh();
      },
      onError: () => {
        message.error('Market value backfill failed. Check server logs.');
      },
    }
  );

  const { post: triggerCashSync, isLoading: isCashSyncing } = usePostApi(
    syncCashBalanceApiCall,
    {
      onSuccess: (data: any) => {
        const result = data?.data;
        const added = result?.rows_added ?? 0;
        message.success(
          added > 0
            ? `Cash balance sync complete — ${added} new row(s). Now re-run Backfill Market Value.`
            : `Cash balance sync complete — no new rows (already in sync).`
        );
        if (onRefresh) onRefresh();
      },
      onError: () => {
        message.error('Cash balance sync failed. Check server logs.');
      },
    }
  );

  if (!item) return null;

  // API response is flat — program fields live at item root (not item.program)
  const program = item;
  const perf = item.performance ?? {};
  const portfolioDaily: any[] = item.portfolio_daily ?? [];
  const balanceHistory: any[] = item.balance_history ?? [];

  const statusLabel = program.status === 1 ? 'Active' : 'Closed';
  const statusColor = program.status === 1 ? 'green' : 'default';

  return (
    <div className="space-y-6">
      {/* ── Program Header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            {program.user_name || 'Wealth Program'}
          </h2>
        </div>
        <Space>
          <Tag color={statusColor}>{statusLabel}</Tag>
          <Button>
            <Link to={`/investment/users/${program.user_id}`}>
              View Customer
            </Link>
          </Button>
          <Tooltip title="Pull deposit/withdrawal history from NHSV into stock_cash_balance_histories — re-run Backfill Market Value afterwards">
            <Button
              icon={<DownloadCloudIcon size={14} />}
              loading={isCashSyncing}
              onClick={() => triggerCashSync({ id: program.id })}
            >
              Sync Cash from NHSV
            </Button>
          </Tooltip>
          <Tooltip title="Recompute daily portfolio market value from stock order history × price data">
            <Button
              icon={<DatabaseIcon size={14} />}
              loading={isMvBackfilling}
              onClick={() => triggerMvBackfill({ id: program.id })}
            >
              Backfill Market Value
            </Button>
          </Tooltip>
          <Button type="primary">
            <Link to={`/wealth/programs/${program.id}/edit`}>
              Edit
            </Link>
          </Button>
        </Space>
      </div>

      {mvBackfillResult && (
        <Alert
          type="success"
          message={`Market value backfill completed — ${mvBackfillResult.daily_rows} daily rows`}
          closable
          onClose={() => setMvBackfillResult(null)}
        />
      )}

      {/* ── Overview ───────────────────────────────────────────────────────── */}
      {(() => {
        const base = perf?.inception_nav ?? program.inception_nav;
        const navChangePct = base && perf?.current_nav ? ((perf.current_nav - base) / base) * 100 : null;
        return (
          <div className="space-y-4">
            {/* Key metrics */}
            <div className="flex flex-wrap gap-x-10 gap-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                  Current NAV{perf?.nav_as_of ? ` · ${perf.nav_as_of}` : ''}
                </p>
                <p className="text-2xl font-semibold tabular-nums text-gray-900">{fmtNum(perf?.current_nav, 2)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">NAV Change</p>
                <p className={`text-2xl font-semibold tabular-nums ${pctColor(navChangePct)}`}>{pct(navChangePct)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Current AUM</p>
                <p className="text-2xl font-semibold tabular-nums text-gray-900">{fmtNum(perf?.current_aum, 0)}</p>
              </div>
            </div>

            {/* Supporting details */}
            <div className="flex flex-wrap gap-x-8 gap-y-3 pt-3 border-t border-gray-100">
              <Stat label="Inception NAV" value={fmtNum(base, 2)} />
              <Stat label="Units Outstanding" value={fmtNum(perf?.units_outstanding, 2)} />
              <Stat label="Start Date" value={program.start_date?.slice(0, 10) || '—'} />
              <Stat label="Period" value={perf?.from_date ? `${perf.from_date} → ${perf.to_date}` : '—'} />
              <Stat label="Perf Fee Rate" value={`${((program.perf_fee_rate ?? 0) * 100).toFixed(0)}%`} />
              <Stat label="Hurdle Rate" value={`${((program.perf_fee_hurdle_rate ?? 0) * 100).toFixed(1)}%`} />
              <Stat label="Total Deposited" value={fmtNum(perf?.total_deposited ?? 0, 0)} />
              <Stat label="Total Withdrawn" value={fmtNum(perf?.total_withdrawn ?? 0, 0)} />
            </div>
          </div>
        );
      })()}

      <AdvisorFeeSection programId={program.id} />

      {/* ── Market Value History Chart (wealth_portfolio_daily) ────────────── */}
      {portfolioDaily.length > 0 && (
        <>
          <h3 className="text-base font-medium text-gray-800 border-b pb-1 flex items-center gap-1">
            <DatabaseIcon size={16} />
            Market Value History ({portfolioDaily.length} days)
          </h3>
          <div className="text-xs text-gray-400 flex gap-4 mb-1">
            <span className="flex items-center gap-1">
              <span className="inline-block w-6 h-0.5 bg-emerald-200 border-dashed border-t" /> Carried forward (non-trading day)
            </span>
          </div>
          <MarketValueChart data={portfolioDaily} />

          {/* Record listing — oldest first, paginated */}
          <Table
            columns={portfolioDailyColumns}
            dataSource={portfolioDaily}
            rowKey="date"
            size="small"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} days`,
            }}
            scroll={{ x: 'max-content' }}
            className="mt-2"
          />
        </>
      )}

      {portfolioDaily.length === 0 && (
        <Alert
          type="info"
          message="No market value history"
          description='Click "Backfill Market Value" to compute daily portfolio values from stock order history × price data.'
          showIcon
        />
      )}

      {/* ── Balance History (deposits & withdrawals from NHSV) ──────────────── */}
      <h3 className="text-base font-medium text-gray-800 border-b pb-1 flex items-center gap-1">
        <ArrowDownCircleIcon size={16} />
        Balance History ({balanceHistory.length} {balanceHistory.length === 1 ? 'entry' : 'entries'})
      </h3>
      {balanceHistory.length > 0 ? (
        <Table
          columns={balanceHistoryColumns}
          dataSource={balanceHistory}
          rowKey="id"
          size="small"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} entries`,
          }}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <Alert
          type="info"
          message="No balance history"
          description='Click "Sync Cash from NHSV" to pull deposit and withdrawal records for this user.'
          showIcon
        />
      )}

    </div>
  );
};

export default ItemDetail;
